import { animate, cubicBezier } from 'animejs';
import utils from '../utils/utils';

class PageLoad {
  constructor() {
    const origin = window.location.origin;
    let isNavigating = false;

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    const transition = document.querySelector('.transition');
    const rect = transition?.querySelector('.rect');
    const lotusBg = transition?.querySelector('.bg');

    const animP = (target, props) => new Promise(res =>
      animate(target, { ...props, onComplete: res })
    );

    const isNavOpen = () => document.querySelector('header')?.classList.contains('nav_open');

    document.addEventListener('click', (e) => {
      if (isNavigating) return;

      const link = e.target.closest('a[href]');
      if (!link) return;

      const attr = link.getAttribute('href');
      if (!attr || attr.startsWith('#')) return;
      if (link.classList.contains('no_ajax')) return;
      if (link.target === '_blank') return;

      let url;
      try { url = new URL(link.href); } catch { return; }

      if (url.origin !== origin) return;
      if (url.pathname.startsWith('/wp-admin') || url.pathname.startsWith('/wp-login')) return;

      e.preventDefault();

      const current = new URL(window.location.href);
      if (url.pathname === current.pathname && url.search === current.search) {
        if (isNavOpen()) window.__nav?.close();
        return;
      }

      history.pushState(null, null, link.href);
      navigate(link.href, link);
    });

    window.addEventListener('popstate', () => {
      if (!isNavigating) navigate(window.location.href, null);
    });

    const navigate = (url) => {
      isNavigating = true;
      utils.disableScroll();

      if (isNavOpen()) {
        window.__nav?.close();
        utils.disableScroll();
      }

      const fetchPromise = fetchHTML(url);

      if (!transition || !rect) {
        fetchPromise
          .then(newDoc => newDoc ? applyContent(newDoc) : null)
          .then(() => {
            if (typeof window.initApp === 'function') window.initApp();
            utils.enableScroll();
            isNavigating = false;
            window.__nav?.refreshSections();
            window.__nav?.syncPosition();
          });
        return;
      }

      // ── PHASE 1 — Transition IN ──────────────────────────────────────────────
      // Reset rect opacity before making the overlay visible (no flash from prev nav)
      if (rect) rect.style.opacity = '0';
      // Lotus: remove anime-init after 400ms so it animates from large→small (CSS transition)
      if (lotusBg) setTimeout(() => lotusBg.classList.remove('anime-init'), 200);
      // Show overlay
      transition.style.visibility = 'visible';
      // Rect fades in (opacity 0 → 1). Runs in parallel with fetch.
      const introAnim = animP(rect, {
        opacity: [0, 1],
        duration: 1000,
        ease: cubicBezier(0.567, -0.013, 0.493, 0.98),
      });

      const preloadPromise = fetchPromise.then(newDoc => newDoc ? preloadHeroMedia(newDoc) : null);
      const safety = new Promise(res => setTimeout(res, 2000));

      // ── PHASE 2 — Content swap ───────────────────────────────────────────────
      // Wait for rect fully opaque AND fetch done, then swap DOM behind the overlay
      Promise.all([introAnim, fetchPromise]).then(([, newDoc]) => {
        if (!newDoc) return Promise.resolve();
        window.__smoothScroll?.scrollTo(0, { immediate: true });
        // ▼▼▼ HERE: position wrapper off-screen before swap so new content
        //     isn't visible when it's injected (overlay is covering everything)
        const wrapper = document.querySelector('.wrapper');
        if (wrapper){ 
          wrapper.style.transform = 'translateY(100vh)';
          wrapper.style.zIndex = '999999999';
        }
        return Promise.race([preloadPromise, safety]).then(() => applyContent(newDoc)).then(waitForFirstSectionMedia);

      // ── PHASE 3 — Transition OUT + page reveal ───────────────────────────────
      // Overlay is still fully covering. New content is in the wrapper, off-screen.
      // ▼▼▼ HERE: animate the reveal however you want.
      // Current: lotus animates out (anime-init), rect fades out, wrapper slides up.
      }).then(() => {
        const wrapper = document.querySelector('.wrapper');
        // Lotus out: add anime-init → CSS transition expands it back to large/invisible
        if (lotusBg) lotusBg.classList.add('anime-init');
        // Hide nav instantly so slideHeaderIn can animate it back in after
        window.__nav?.resetInstant();
        // Rect fades out in parallel (not awaited — same duration as wrapper slide)
        // animP(rect, { opacity: [1, 0], duration: 1200, ease: 'linear' });
        // Wrapper slides up from below — ▼▼▼ THIS IS THE MAIN REVEAL ANIMATION
        return animP(wrapper, {
          opacity: [0.3, 1],
          y: ['100vh', '0px'],
          duration: 1200,
          ease: cubicBezier(0.567, -0.013, 0.493, 0.98),
        });

      // ── RESET ────────────────────────────────────────────────────────────────
      }).then(() => {
        const wrapper = document.querySelector('.wrapper');
        // Clear all inline styles set during the animation (hand control back to CSS)
        if (wrapper) { wrapper.style.transform = ''; wrapper.style.zIndex = ''; }
        // Reset rect to CSS default (opacity:0) for the next navigation
        if (rect) rect.style.opacity = '';
        // Reset lotus to large/invisible state (CSS default with anime-init)
        if (lotusBg) lotusBg.classList.add('anime-init');
        // Hide overlay
        transition.style.visibility = 'hidden';
        // Re-init page scroll animations now that the wrapper is in its final position
        if (typeof window.initApp === 'function') window.initApp();
        utils.enableScroll();
        isNavigating = false;
        window.__nav?.refreshSections();
        window.__nav?.syncPosition();
        // Slide header back in from above
        window.__nav?.slideHeaderIn();
      }).catch(err => {
        console.error('Navigation error:', err);
        window.location.href = url;
      });
    };

    const fetchHTML = async (url) => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        return new DOMParser().parseFromString(html, 'text/html');
      } catch (err) {
        console.error('Fetch error:', err);
        window.location.href = url;
        return null;
      }
    };

    const applyContent = (newDoc) => {
      const currentWrapper = document.querySelector('.wrapper');
      const newWrapper = newDoc.querySelector('.wrapper');

      if (currentWrapper && newWrapper) {
        currentWrapper.innerHTML = newWrapper.innerHTML;
        Array.from(newWrapper.querySelectorAll('script'))
          .filter(s => !s.src && s.textContent.trim())
          .forEach(old => {
            const script = document.createElement('script');
            if (old.type) script.type = old.type;
            script.textContent = old.textContent;
            currentWrapper.appendChild(script);
          });
      }

      document.title = newDoc.title;
      document.body.className = newDoc.body.className;
      window.scrollTo(0, 0);

      Array.from(newDoc.querySelectorAll('script:not([src])'))
        .filter(s => {
          if (!s.textContent.trim()) return false;
          if (newWrapper?.contains(s)) return false;
          if (s.id && document.getElementById(s.id)) return false;
          return true;
        })
        .forEach(s => {
          const el = document.createElement('script');
          if (s.id) el.id = s.id;
          if (s.type) el.type = s.type;
          el.textContent = s.textContent;
          document.head.appendChild(el);
        });

      const pending = Array.from(newDoc.querySelectorAll('script[src]'))
        .filter(s => !document.querySelector(`script[src="${s.src}"]`))
        .map(s => new Promise(resolve => {
          const el = document.createElement('script');
          el.src = s.src;
          el.onload = resolve;
          el.onerror = resolve;
          document.head.appendChild(el);
        }));

      return Promise.all(pending);
    };

    // After applyContent: find the hero video, load its source if lazy (data-src → src),
    // force play() so the video is actually running before the wrapper slides in.
    // Images are already handled by preloadHeroMedia (runs before applyContent).
    const waitForFirstSectionMedia = () => new Promise(resolve => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;

      // .anime-hero-media is the hero media container class (module-01-hero.php line 51)
      const heroMedia = document.querySelector('.anime-hero-media');
      if (!heroMedia) return resolve();

      // Pick the breakpoint-appropriate video
      let video = heroMedia.querySelector(isMobile ? 'video.mobile' : 'video.desktop');
      if (!video) video = heroMedia.querySelector('video:not(.mobile):not(.desktop)');
      if (!video) return resolve();

      // Trigger lazy-load for data-src videos (desktop≠mobile case)
      const source = video.querySelector('source[data-src]');
      if (source && !source.src) {
        source.src = source.dataset.src;
        video.load();
      }

      // Called when data is ready: force play even while wrapper is off-screen,
      // so the video is already running by the time the reveal animation completes.
      const start = () => { video.play().catch(() => {}); resolve(); };

      if (video.readyState >= 3) return start();

      const timeout = setTimeout(resolve, 3000); // safety: reveal anyway after 3s
      video.addEventListener('canplay', () => { clearTimeout(timeout); start(); }, { once: true });
      video.addEventListener('error',   () => { clearTimeout(timeout); resolve(); }, { once: true });
    });

    const preloadHeroMedia = (newDoc) => {
      const imgs = [...newDoc.querySelectorAll('.hero-media img, .bg_media img')];
      return Promise.all(imgs.map(el => new Promise(res => {
        const source = el.closest('picture')?.querySelector('source[srcset]');
        const srcset = source?.getAttribute('srcset') ?? el.getAttribute('srcset') ?? '';
        const sizes  = source?.getAttribute('sizes')  ?? el.getAttribute('sizes')  ?? '';
        const src    = el.getAttribute('src') || '';
        if (!src && !srcset) return res();
        const img = new Image();
        img.onload  = res;
        img.onerror = res;
        if (srcset) { img.srcset = srcset; img.sizes = sizes; }
        img.src = src;
      })));
    };
  }
}

export { PageLoad };
